import type { CSSProperties, ReactNode } from 'react';

/**
 * Field — a label, its control, and an optional hint or error, stacked as one column.
 * Ported from views/components/form-field.ejs.
 *
 * The field owns the column (`.field`: 4px gap, the control fills it at 38px) and the label's type, so a
 * page never sets width:100% on an input or repeats the label's classes.
 */
export type FieldProps = {
  /** The caption (plain text unless `label` carries markup). */
  label?: ReactNode;
  /** The control's id — the label's `for`, so clicking it focuses the control. */
  htmlFor?: string;
  required?: boolean;
  /** A muted line under the control. */
  hint?: ReactNode;
  /** An error line under the control; it replaces the hint, as the EJS component does. */
  error?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** The rendered control (input, select, textarea, or a row of them). */
  children?: ReactNode;
};

export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  className,
  style,
  children,
}: FieldProps) {
  return (
    <div className={className ? `field ${className}` : 'field'} style={style}>
      <label htmlFor={htmlFor}>
        {label}
        {/* The space is the page's: form-field.ejs writes `label + ' ' + <span class="req">*</span>`. */}
        {required ? <> <span className="req">*</span></> : null}
      </label>
      {children}
      {error ? (
        <div className="field-hint field-error t-small">{error}</div>
      ) : hint ? (
        <div className="field-hint muted t-small">{hint}</div>
      ) : null}
    </div>
  );
}
